/**
 * Constraint filtering utilities.
 *
 * Uses the same collision key format as the bathroom analyzer pipeline:
 *   "physical:sink:toilet"              — physical overlap (types sorted alphabetically)
 *   "clearance:sink_phys:toilet_clr"    — sink's body in toilet's clearance zone
 *   "clearance:toilet_phys:sink_clr"    — toilet's body in sink's clearance zone
 *   "clearance_boundary:shower"         — shower clearance outside room
 *   "physical_boundary:sink"            — sink extends outside room
 *   "door_blocking:toilet"              — toilet blocks door swing
 *   "back_edge:sink"                    — sink back edge intersects wall
 */

function normalizeType(type) {
  return (type || '').toLowerCase();
}

/**
 * Build a collision rule key from a collision and the fixture being checked.
 * Matches the pipeline's collisionKey format from 5-preValidation.js.
 *
 * @param {Object} collision - Collision from checkPlacement
 * @param {string} fixtureType - Type of the fixture being checked
 * @returns {string} Rule key
 */
export function collisionToRule(collision, fixtureType) {
  const source = normalizeType(fixtureType);
  const collType = collision.type;
  const target = normalizeType(collision.target?.type || '');

  if (collType === 'fixture-to-fixture') {
    const sorted =
      source < target ? `${source}:${target}` : `${target}:${source}`;
    return `physical:${sorted}`;
  }

  if (collType === 'fixture-clearance') {
    // source's body is in target's clearance
    return `clearance:${source}_phys:${target}_clr`;
  }

  if (collType === 'clearance-blocked') {
    // source's clearance is blocked by target's body
    return `clearance:${target}_phys:${source}_clr`;
  }

  if (collType === 'clearance-boundary') {
    return `clearance_boundary:${source}`;
  }

  if (collType === 'door-swing') {
    return `door_blocking:${source}`;
  }

  if (collType === 'parent-wall-bounds') {
    return `back_edge:${source}`;
  }

  if (collType === 'physical-boundary') {
    return `physical_boundary:${source}`;
  }

  // Fallback for unknown types
  if (target) {
    return `${collType}:${source}:${target}`;
  }
  return `${collType}:${source}`;
}

/**
 * Get a human-readable label for a collision rule.
 */
export function ruleToLabel(rule) {
  const parts = rule.split(':');

  // physical:sink:toilet
  if (parts[0] === 'physical' && parts.length === 3) {
    return `${parts[1]} overlaps ${parts[2]}`;
  }

  // clearance:sink_phys:toilet_clr
  if (parts[0] === 'clearance' && parts.length === 3) {
    const source = parts[1].replace('_phys', '');
    const target = parts[2].replace('_clr', '');
    return `${source} body in ${target} clearance`;
  }

  // clearance_boundary:shower
  if (parts[0] === 'clearance_boundary') {
    return `${parts[1]} clearance outside room`;
  }

  // physical_boundary:sink
  if (parts[0] === 'physical_boundary') {
    return `${parts[1]} extends outside room`;
  }

  // door_blocking:toilet
  if (parts[0] === 'door_blocking') {
    return `${parts[1]} blocks door swing`;
  }

  // back_edge:sink
  if (parts[0] === 'back_edge') {
    return `${parts[1]} back edge intersects wall`;
  }

  return rule;
}

/**
 * Filter collisions, removing those matching disabled rules.
 *
 * @param {Array} collisions - Collisions returned by checkPlacement
 * @param {string} fixtureType - Type of the fixture being checked
 * @param {string[]} disabledRules - Specific rule strings to skip
 * @returns {Array} Filtered collisions
 */
export function filterCollisions(collisions, fixtureType, disabledRules) {
  if (!disabledRules || disabledRules.length === 0) return collisions;
  const disabled = new Set(disabledRules);
  return collisions.filter(
    (c) => !disabled.has(collisionToRule(c, fixtureType))
  );
}

/**
 * Map disabled rules to checkPlacement skipChecks strings.
 * All filtering happens post-checkPlacement via filterCollisions.
 *
 * @returns {string[]} Always empty — filtering is done post-check.
 */
export function toSkipChecks() {
  return [];
}
